import React, { createContext, useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import deploymentWebSocketService from '../services/deploymentWebSocketService';
import { useAuth } from './AuthContext';

const DeploymentWebSocketContext = createContext(null);

/**
 * Hook: activates WebSocket connection when a consumer mounts.
 */
export const useDeploymentWebSocket = () => {
  const context = useContext(DeploymentWebSocketContext);
  if (!context) {
    throw new Error('useDeploymentWebSocket must be used within DeploymentWebSocketProvider');
  }
  useEffect(() => {
    context._activate();
    return () => context._deactivate();
  }, [context]);
  return context;
};

export const DeploymentWebSocketProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [connected, setConnected] = useState(false);
  const consumerCountRef = useRef(0);
  const [active, setActive] = useState(false);

  const activate = useCallback(() => {
    consumerCountRef.current += 1;
    if (consumerCountRef.current === 1) setActive(true);
  }, []);

  const deactivate = useCallback(() => {
    consumerCountRef.current = Math.max(0, consumerCountRef.current - 1);
    if (consumerCountRef.current === 0) setActive(false);
  }, []);

  useEffect(() => {
    if (isAuthenticated && active) {
      deploymentWebSocketService.connect().catch(err =>
        console.error('[DeployWS] Connect failed:', err));
      const unsub = deploymentWebSocketService.addConnectionListener(setConnected);
      return () => { unsub(); deploymentWebSocketService.disconnect(); };
    } else {
      deploymentWebSocketService.disconnect();
      setConnected(false);
    }
  }, [isAuthenticated, active]);

  const onDeploymentUpdate = useCallback((callback) => {
    return deploymentWebSocketService.onDeploymentUpdate(callback);
  }, []);

  const onNodeDeploymentUpdate = useCallback((nodeId, callback) => {
    return deploymentWebSocketService.onNodeDeploymentUpdate(nodeId, callback);
  }, []);

  const value = useMemo(() => ({
    connected,
    isConnected: connected,
    isPolling: deploymentWebSocketService.isPolling(),
    onDeploymentUpdate,
    onNodeDeploymentUpdate,
    service: deploymentWebSocketService,
    _activate: activate,
    _deactivate: deactivate,
  }), [connected, onDeploymentUpdate, onNodeDeploymentUpdate, activate, deactivate]);

  return (
    <DeploymentWebSocketContext.Provider value={value}>
      {children}
    </DeploymentWebSocketContext.Provider>
  );
};

export default DeploymentWebSocketContext;
